<script setup>
/**
 * FormControlStockQuantity
 * Dual-state stock allocation modifier input block.
 * Toggles structural inventory tracking behaviors natively, managing
 * synchronized background background checks to display quantities currently locked in carts.
 */
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue';
import FormControlTypeNumber from '../form-control-type/FormControlTypeNumber.vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State Layers ---

const fieldEnabled = ref(Number.isInteger(props.currentData?.stockQuantity));
const oldValue = ref(0);
const basketCount = ref(0);

// Unified structural tracker placeholder for polling management
let pollingTimerReference = null;

// Template element linkers
const innerInputComponentRef = ref(null);

// --- Synchronous Computeds ---

const isTracked = computed(() => fieldEnabled.value);

const tableName = computed(() => store.state[props.formId]?.tableName);

const entityId = computed(() => store.state[props.formId]?.id);

const componentId = computed(() => store.state.components?.componentIds?.[tableName.value]);

// --- Methods ---

/**
 * Dispatches background operations to check remote item allocation logs.
 * Runs on a self-chaining sequence to prevent race condition network congestion.
 */
const executeBasketCountFetch = async () => {
    // Break operational chains early if structural limits are dropped
    if (!isTracked.value || !componentId.value || !entityId.value) return;

    try {
        const response = await store.dispatch('request/get', {
            url: `api/order/basket-quantity/${componentId.value}/${entityId.value}`
        });

        basketCount.value = response?.data?.quantity || 0;
    } catch (err) {
        console.error('Failed to resolve dynamic real-time basket quantities:', err);
    } finally {
        // Chained execution: Schedules subsequent check cycles only after the previous request settles
        if (isTracked.value) {
            pollingTimerReference = setTimeout(executeBasketCountFetch, 1000);
        }
    }
};

/**
 * Safely halts active network polling procedures and clears scheduled timers.
 */
const clearActivePollingLoops = () => {
    if (pollingTimerReference) {
        clearTimeout(pollingTimerReference);
        pollingTimerReference = null;
    }
};

/**
 * Toggles explicit tracking assignments.
 * Seamlessly caches previous integers to prevent accidental data entry deletion.
 */
const handleTrackStockToggle = () => {
    fieldEnabled.value = !fieldEnabled.value;

    const absoluteStockValue = props.currentData?.stockQuantity;

    if (absoluteStockValue !== null && absoluteStockValue !== undefined) {
        oldValue.value = absoluteStockValue;
    }

    // Set or drop the structural quantity parameter mapping
    props.currentData.stockQuantity = absoluteStockValue === null ? oldValue.value : null;

    if (fieldEnabled.value) {
        nextTick(() => {
            // Safe Access: Checks native inputs directly, falling back gracefully across builds
            if (innerInputComponentRef.value) {
                const rawInputElement = innerInputComponentRef.value.$el?.querySelector('input') || innerInputComponentRef.value.$el;
                rawInputElement?.select?.();
            }
        });
    }
};

// --- Structural Watchers & Lifecycles ---

watch(isTracked, (newValue) => {
    clearActivePollingLoops();
    if (newValue) {
        // Kick off safe chained background executions
        executeBasketCountFetch();
    }
}, { immediate: true });

onBeforeUnmount(() => {
    // Explicit garbage collection cleanup to prevent background memory leaks
    clearActivePollingLoops();
});
</script>

<template>
    <div class="d-flex align-items-center form-control-stock-quantity-container">

        <input
            :id="field.id"
            :checked="isTracked"
            type="checkbox"
            class="form-check-input"
            @click="handleTrackStockToggle"
        >

        <FormControlTypeNumber
            ref="innerInputComponentRef"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
            class="ms-2"
        />

        <span
            v-if="basketCount && isTracked"
            class="ms-2 badge bg-info-subtle text-info border border-info-subtle px-2 py-1 small"
        >
            Basket count: {{ basketCount }}
        </span>

    </div>
</template>